<!--
    Visor de pictogramas basado en Three.js.
    Consume el output de cad/rendering/DxfScene.js.

    Esta carpeta reemplaza a viewer-konva/ y viewer-pixi/.
    Ambos quedan como legacy y se eliminarán cuando se complete la migración.
  -->

<template>
  <div ref="canvasContainer" class="canvasContainer" :class="{ 'is-pan': panCursor }"></div>
</template>

<script>
import * as three from "three"
import opentype from "opentype.js"
import { DxfScene } from "../cad/rendering/DxfScene"
import { DxfViewer } from "./DxfViewer"

import { createLogger } from "../utils/logger"
const logger = createLogger('Viewer')

export default {
  name: 'CadViewer',

  props: {
    // cadJson producido por cad/formats/loadFromFile. Null hasta que el usuario suba un archivo.
    cadJson: {
      type: Object,
      default: null
    },
    /** Array de URLs a archivos .ttf. Se convierten en fontFetchers para el TextRenderer. */
    fonts: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      dxfViewer: null,  // Instancia vanilla de DxfViewer. Se crea en mounted(), se destruye en beforeDestroy().
      // Cursor "manito" (grab/grabbing) cuando el modo pan está activo. Por
      // defecto true porque el pan (click-izquierdo) está activo desde el inicio.
      panCursor: true,
    }
  },

  mounted() {
    this.dxfViewer = new DxfViewer(this.$refs.canvasContainer, {
      autoResize:  true,
      clearColor:  new three.Color('#ffffff'),  // fondo blanco (estilo AutoCAD light)
      antialias:   true,
      // Habilita canal alpha en el WebGL context. No afecta la vista normal porque
      // clearColor sigue siendo blanco opaco, pero permite que Save({transparent:true})
      // produzca capturas con fondo transparente real.
      canvasAlpha: true,
    })

    this._setupEventForwarding()

    // Si ya hay cadJson cuando el componente monta, cargarlo
    if (this.cadJson) {
      this._loadCadJson(this.cadJson)
    }
  },

  beforeDestroy() {
    if (this.dxfViewer) {
      this.dxfViewer.Destroy()      // libera WebGL, materiales, observers, etc.
      this.dxfViewer = null
    }
  },

  watch: {
    cadJson(newVal) {
      if (newVal) {
        this._loadCadJson(newVal)
      } else {
        // Si cadJson vuelve a null (usuario clickó "limpiar"), reset el viewer
        this.dxfViewer?.Clear()
      }
    }
  },

  methods: {
    // ───────────────────────────────── API pública ─────────────────────────────────
    // La llama el Toolbar desde LayoutBuilder vía $refs.viewer.X()

    /**
     * Acceso público al DxfViewer vanilla — para que componentes externos
     * (ej. DxfDiffOverlay) puedan llamar GetScene(), GetOrigin(), Render(), etc.
     * y pintar capas extra sobre el render principal.
     */
    GetDxfViewer() {
      return this.dxfViewer
    },

    /** Ajusta cámara para que se vea toda la escena. */
    FitToScreen() {
      if (!this.dxfViewer?.HasRenderer()) return
      const bounds = this.dxfViewer.GetBounds()
      const origin = this.dxfViewer.GetOrigin()
      if (!bounds || !origin) return
      this.dxfViewer.FitView(
        bounds.minX - origin.x,
        bounds.maxX - origin.x,
        bounds.minY - origin.y,
        bounds.maxY - origin.y
      )
    },

    /** Zoom in con factor x1.2 sobre la cámara. */
    ZoomIn() {
      this._zoomBy(1.2)
    },

    /** Zoom out con factor x0.83 sobre la cámara. */
    ZoomOut() {
      this._zoomBy(1 / 1.2)
    },

    _zoomBy(factor) {
      const cam = this.dxfViewer?.GetCamera()
      if (!cam) return
      cam.zoom *= factor
      cam.updateProjectionMatrix()
      this.dxfViewer.Render()
    },

    /**
     * Modo PAN dedicado (herramienta mano, estilo Excalidraw). El arrastre para
     * mover el lienzo está SIEMPRE disponible (OrbitControls); este método solo
     * refleja el modo en el CURSOR (manito grab). El bloqueo de interactividad
     * (clicks/tooltip) mientras se arrastra lo gestiona el host (CadStudio): en
     * modo mano los apaga del todo, y en modo interacción los suprime solo
     * durante el arrastre activo.
     */
    EnableDraggable(enabled) {
      this.panCursor = !!enabled
    },

    /** Forza un render frame. */
    Render() {
      this.dxfViewer?.Render()
    },

    /**
     * Captura del viewer como PNG base64.
     *
     * Renderiza a un WebGLRenderTarget OFFSCREEN (no toca el canvas visible):
     *   - Sin parpadeo en pantalla (antes se cambiaba pixelRatio/clear del canvas real).
     *   - Lectura determinista con `readRenderTargetPixels` → evita el "destello"
     *     (capturar el canvas con antialias + preserveDrawingBuffer:false devolvía
     *     a veces el buffer blanco sin resolver la geometría).
     *   - MSAA (samples) para bordes nítidos y color igual al de pantalla
     *     (texture.colorSpace = outputColorSpace del renderer).
     *
     * @param {Object}  opts
     * @param {number}  opts.scale       - multiplicador de resolución (1 = normal, 2 = nítido). Default 2.
     * @param {boolean} opts.transparent - true para fondo transparente. Default true.
     */
    Save({ scale = 2, transparent = true } = {}) {
      const renderer = this.dxfViewer?.GetRenderer?.()
      const scene    = this.dxfViewer?.GetScene?.()
      const camera   = this.dxfViewer?.GetCamera?.()
      const canvas   = this.dxfViewer?.GetCanvas?.()
      if (!renderer || !scene || !camera || !canvas) return null

      // Tamaño offscreen: tamaño visible (CSS px) × scale. Mismo aspect ratio →
      // el encuadre (cámara ortográfica) coincide sin tocar la cámara.
      const w  = canvas.clientWidth
      const h  = canvas.clientHeight
      const tw = Math.max(1, Math.round(w * scale))
      const th = Math.max(1, Math.round(h * scale))

      // Estado del renderer a restaurar (NO tocamos pixelRatio/size → sin parpadeo).
      const prevTarget     = renderer.getRenderTarget()
      const prevClearColor = renderer.getClearColor(new three.Color())
      const prevClearAlpha = renderer.getClearAlpha()

      // RenderTarget offscreen con MSAA. La textura usa el colorSpace de salida
      // del renderer para que los colores coincidan con la vista en pantalla.
      const rt = new three.WebGLRenderTarget(tw, th, { samples: 4 })
      rt.texture.colorSpace = renderer.outputColorSpace

      try {
        // Fondo: transparente (0,0,0,0) o blanco opaco como la vista normal.
        if (transparent) renderer.setClearColor(0x000000, 0)
        else             renderer.setClearColor(0xffffff, 1)

        renderer.setRenderTarget(rt)
        renderer.render(scene, camera)   // autoClear limpia el target con el clear actual

        const pixels = new Uint8Array(tw * th * 4)
        renderer.readRenderTargetPixels(rt, 0, 0, tw, th, pixels)

        // Con fondo transparente, los bordes AA quedan premultiplicados → los
        // des-premultiplicamos para que no salgan oscuros en el PNG.
        return this._PixelsToPngDataUrl(pixels, tw, th, transparent)
      } finally {
        // Restaurar siempre. El canvas visible no se tocó → no re-renderizamos.
        renderer.setRenderTarget(prevTarget)
        renderer.setClearColor(prevClearColor, prevClearAlpha)
        rt.dispose()
      }
    },

    /**
     * Convierte un buffer RGBA de WebGL (origen abajo-izquierda) en un PNG dataURL.
     * Voltea verticalmente (WebGL → canvas 2D) y, si `unpremultiply`, divide el
     * color por el alpha en los píxeles semitransparentes (bordes AA).
     */
    _PixelsToPngDataUrl(pixels, w, h, unpremultiply) {
      const out = document.createElement('canvas')
      out.width  = w
      out.height = h
      const ctx = out.getContext('2d')
      const img = ctx.createImageData(w, h)
      const dst = img.data
      const rowBytes = w * 4
      for (let y = 0; y < h; y++) {
        const srcRow = (h - 1 - y) * rowBytes  // fila espejada
        const dstRow = y * rowBytes
        for (let x = 0; x < w; x++) {
          const s = srcRow + x * 4
          const d = dstRow + x * 4
          let r = pixels[s], g = pixels[s + 1], b = pixels[s + 2]
          const a = pixels[s + 3]
          if (unpremultiply && a !== 0 && a !== 255) {
            const inv = 255 / a
            r = r * inv; if (r > 255) r = 255
            g = g * inv; if (g > 255) g = 255
            b = b * inv; if (b > 255) b = 255
          }
          dst[d] = r; dst[d + 1] = g; dst[d + 2] = b; dst[d + 3] = a
        }
      }
      ctx.putImageData(img, 0, 0)
      return out.toDataURL('image/png')
    },

    /** Lista de capas (para futuro panel de visibilidad). */
    GetLayers(nonEmptyOnly = false) {
      return this.dxfViewer?.GetLayers(nonEmptyOnly) ?? []
    },

    /** Toggle visibilidad de una capa. */
    ShowLayer(name, show) {
      this.dxfViewer?.ShowLayer(name, show)
    },

    /** Cambia opacidad de todas las entities de una capa. */
    SetLayerOpacity(name, opacity) {
      this.dxfViewer?.SetLayerOpacity(name, opacity)
    },

    /** Pinta una capa en gris uniforme (on=true) o restaura su color (on=false). */
    SetLayerGrayscale(name, on, gray) {
      this.dxfViewer?.SetLayerGrayscale(name, on, gray)
    },

    async _loadCadJson(cadJson) {
      try {
        // 1. Construir DxfScene a partir del cadJson.
        //    Se hace en main thread (es sincrónico/CPU-bound; el parser ya está hecho).
        const builder = new DxfScene()
        const fetchers = this._createFontFetchers(this.fonts)
        await builder.Build(cadJson, fetchers)
        // 2. Pasar la escena resultante al DxfViewer (vanilla).
        await this.dxfViewer.LoadScene(builder.scene, cadJson)
      } catch (err) {
        logger.error('load cadJson error:', err)
        this.$emit('epc-error', err.message)
      }
    },

    /**
     * Conecta los eventos custom del DxfViewer al sistema $emit de Vue.
     * El DxfViewer emite `__dxf_loaded`, `__dxf_pointerdown`, etc.
     * Aquí los re-emitimos como `epc-loaded`, `epc-pointerdown`, etc.
     */
    _setupEventForwarding() {
      const events = [
        'loaded',
        'cleared',
        'destroyed',
        'resized',
        'pointerdown',
        'pointerup',
        'viewChanged',
        'message',
      ]
      for (const ev of events) {
        this.dxfViewer.Subscribe(ev, (e) => {
          this.$emit('epc-' + ev, e.detail)
        })
      }
    },

    /**
     * Convierte URLs de archivos .ttf en "font fetchers" — funciones async que
     * descargan el TTF y lo parsean con opentype.js. Los invoca el TextRenderer
     * por demanda solo cuando encuentra glyphs sin resolver.
     */
    _createFontFetchers(urls) {
      if (!urls || urls.length === 0) return null
      return urls.map(url => async () => {
        const data = await fetch(url).then(r => r.arrayBuffer())
        return opentype.parse(data)
      })
    }
  }
}
</script>

<style scoped>
  .canvasContainer {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  /* Cursor "manito" (el <canvas> hereda el cursor del contenedor):
     - En reposo con el modo pan activo → manito abierta (grab).
     - Al pulsar/arrastrar SIEMPRE → manito cerrada (grabbing), porque el pan
       con click-izquierdo sigue disponible aunque el toggle esté desactivado.
     - En reposo con pan desactivado → cursor por defecto (puntero). */
  .canvasContainer.is-pan { cursor: grab; }
  .canvasContainer:active { cursor: grabbing; }
</style>
